<script setup lang="ts">
/**
 * UserMenu - top-right identity cluster (avatar + name + role) with a dropdown
 * exposing the profile, organization settings and logout actions.
 *
 * Part of the UI refresh (#2749): this cluster moved out of the bottom of the
 * sidebar into the header so the sidebar can collapse to a slim icon rail.
 */
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import { ChevronDown, User, Building2, LogOut } from 'lucide-vue-next'
import { useAuthStore } from '@/stores/auth'
import { useUserProfileStore } from '@/stores/userProfile'
import { usePermissions } from '@/composables/usePermissions'
import { Permission } from '@/utils/permissionBitmask'
import Avatar from '@/components/atoms/Avatar.vue'
import { logUnexpectedFailure } from '@/utils/errorHandling'

const { t } = useI18n()
const router = useRouter()
const authStore = useAuthStore()
const userProfileStore = useUserProfileStore()
const { hasPermission } = usePermissions()

const isOpen = ref(false)
const menuRef = ref<HTMLElement | null>(null)

const fullName = computed(() => userProfileStore.fullName)
const role = computed(() => userProfileStore.profile?.functionTitle ?? '')
const avatarUrl = computed(() => userProfileStore.profile?.avatarUrl ?? null)
const canManageOrganization = computed(() =>
  hasPermission(Permission.ORGANIZATION_SETTINGS_READ)
)

function toggle() {
  isOpen.value = !isOpen.value
}

function close() {
  isOpen.value = false
}

async function handleLogout() {
  close()
  try {
    await authStore.signOut()
    router.push('/login')
  } catch (error) {
    logUnexpectedFailure('Logout failed:', error)
  }
}

function handleClickOutside(event: MouseEvent) {
  if (menuRef.value && !menuRef.value.contains(event.target as Node)) {
    close()
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>

<template>
  <div ref="menuRef" class="user-menu" data-testid="user-menu">
    <button
      type="button"
      class="user-menu__trigger"
      :aria-label="t('userMenu.open')"
      :aria-expanded="isOpen"
      aria-haspopup="menu"
      data-testid="user-menu-trigger"
      @click="toggle"
    >
      <Avatar :src="avatarUrl" :name="fullName" size="sm" />
      <span class="user-menu__identity">
        <span class="user-menu__name">{{ fullName }}</span>
        <span v-if="role" class="user-menu__role">{{ role }}</span>
      </span>
      <ChevronDown
        class="user-menu__chevron"
        :class="{ 'user-menu__chevron--open': isOpen }"
        :size="16"
        aria-hidden="true"
      />
    </button>

    <Transition name="dropdown">
      <div v-if="isOpen" class="user-menu__dropdown">
        <div role="menu">
          <router-link
            to="/profile"
            class="user-menu__item"
            role="menuitem"
            data-testid="user-menu-profile"
            @click="close"
          >
            <User class="user-menu__item-icon" :size="18" aria-hidden="true" />
            <span>{{ t('navigation.profile') }}</span>
          </router-link>

          <router-link
            v-if="canManageOrganization"
            to="/organization"
            class="user-menu__item"
            role="menuitem"
            data-testid="user-menu-organization"
            @click="close"
          >
            <Building2 class="user-menu__item-icon" :size="18" aria-hidden="true" />
            <span>{{ t('navigation.organization') }}</span>
          </router-link>

          <button
            type="button"
            class="user-menu__item user-menu__item--button"
            role="menuitem"
            data-testid="user-menu-logout"
            @click="handleLogout"
          >
            <LogOut class="user-menu__item-icon" :size="18" aria-hidden="true" />
            <span>{{ t('navigation.logout') }}</span>
          </button>
        </div>
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.user-menu {
  position: relative;
  flex: 0 0 auto;
}

.user-menu__trigger {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  max-width: 220px;
  padding: var(--spacing-xs) var(--spacing-sm);
  background: transparent;
  border: 1px solid var(--color-border-default);
  border-radius: var(--radius-full);
  color: var(--color-text-primary);
  cursor: pointer;
  transition: background-color var(--transition-duration-fast) var(--transition-easing-ease-out),
    border-color var(--transition-duration-fast) var(--transition-easing-ease-out);
}

.user-menu__trigger:hover {
  background: var(--color-background-canvas);
  border-color: var(--color-brand-primary);
}

.user-menu__identity {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
  line-height: var(--line-height-tight);
}

.user-menu__name {
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-semibold);
}

.user-menu__role {
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: var(--font-size-xs);
  color: var(--color-text-secondary);
}

.user-menu__chevron {
  flex-shrink: 0;
  color: var(--color-text-secondary);
  transition: transform var(--transition-duration-fast) var(--transition-easing-ease-out);
}

.user-menu__chevron--open {
  transform: rotate(180deg);
}

.user-menu__dropdown {
  position: absolute;
  top: calc(100% + var(--spacing-xs));
  right: 0;
  min-width: 200px;
  padding: var(--spacing-xs);
  background: var(--color-background-content);
  border: 1px solid var(--color-border-default);
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-lg);
  z-index: var(--z-dropdown);
}

.user-menu__item {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  width: 100%;
  padding: var(--spacing-sm) var(--spacing-md);
  border: none;
  border-radius: var(--radius-sm);
  background: transparent;
  color: var(--color-text-primary);
  font-family: inherit;
  font-size: var(--font-size-sm);
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  transition: background-color var(--transition-duration-fast) var(--transition-easing-ease-out);
}

.user-menu__item:hover {
  background: var(--color-background-canvas);
}

.user-menu__item-icon {
  flex-shrink: 0;
  color: var(--color-text-secondary);
}

/* Hide the name/role on narrow viewports - the avatar stays as the trigger. */
@media (--breakpoint-md-down) {
  .user-menu__identity {
    display: none;
  }
}

.dropdown-enter-active,
.dropdown-leave-active {
  transition: opacity var(--transition-duration-fast) var(--transition-easing-ease-out),
    transform var(--transition-duration-fast) var(--transition-easing-ease-out);
}

.dropdown-enter-from,
.dropdown-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
